<template>
  <v-dialog :model-value="modelValue" persistent max-width="700" :fullscreen="$store.state.isMobile">
    <v-card>
      <v-card-title class="d-flex align-center">Save your backup code <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <!-- Passkey-first (INVITE-LINK-PASSKEY-FIRST.md §2.3, 2026-10-08):
             this dialog is only reached when no passkey could be made, or
             from the Account page on request. Say what the code is for and
             what losing it costs — a reset, bounded by the record retention
             window — not "the only way, we cannot help". -->
        <div class="text-subtitle-1 mb-3">
          <template v-if="onRequest">
            Keep this backup code somewhere safe. It unlocks your encrypted data on a new device
            or after clearing this browser, even without your passkey.
          </template>
          <template v-else-if="passkeyRegistered">
            Keep this backup code somewhere safe. It unlocks your encrypted data on a new device
            if your passkey is ever unavailable.
          </template>
          <template v-else>
            This device couldn't create a passkey, so here is a backup code instead. You'll need it to
            unlock your encrypted {{ $store.getters.isViewOnlyPartner ? 'reports' : 'screenshots' }} on a new
            device or after clearing this browser.
          </template>
        </div>
        <!-- An accountability partner who set this up only to view someone
             else's data has no screenshots of their own (2026-10-08). -->
        <v-alert type="info" variant="tonal" density="compact" class="mb-5">
          <template v-if="$store.getters.isViewOnlyPartner">
            If you ever lose it, you can still reset encryption from your Account page. Nothing of yours is
            deleted; the people who share reports with you just approve you again with one click.
          </template>
          <template v-else>
            If you ever lose it, you can still reset encryption from your Account page. A reset deletes your
            encrypted screenshots and events from the last two weeks; nothing else is lost.
          </template>
        </v-alert>

        <v-card variant="outlined" class="pa-4 mb-4 text-center">
          <div class="recovery-code text-h6">{{ code }}</div>
        </v-card>

        <div class="d-flex justify-center flex-wrap mb-5" style="gap: 8px;">
          <!-- Download leads: the unlock dialogs open the saved file first
               ("Load backup code file"), typing the code is the fallback. -->
          <v-btn variant="elevated" color="primary" size="small" @click="download">
            <!-- `file_download`: the classic Material Icons ligature — plain
                 `download` only exists in newer font releases than ours. -->
            <v-icon start>file_download</v-icon>{{ downloaded ? 'Downloaded' : 'Download' }}
          </v-btn>
          <v-btn variant="outlined" size="small" @click="copy">
            <v-icon start>content_copy</v-icon>{{ copied ? 'Copied!' : 'Copy' }}
          </v-btn>
          <!-- A real anchor (v-btn + href) opening in a new tab (user decision
               2026-10-10): the browser hands that tab to the mail program and
               this page — and the code, which lives only in this dialog's
               props — stays put. Inside the Android app the WebView routes
               the link to the system mail chooser either way. -->
          <v-btn v-if="mailtoHref" variant="outlined" size="small" :href="mailtoHref" target="_blank" @click="emailClicked">
            <v-icon start>email</v-icon>Email
          </v-btn>
        </div>
        <div v-if="emailHint" class="text-caption text-medium-emphasis text-center mt-n3 mb-4">
          No email app opened? This browser has no email program linked — use Copy or Download instead.
        </div>

        <v-checkbox
          v-model="acknowledged"
          color="primary"
          density="compact"
          hide-details
          label="I've saved my backup code"
        />
      </v-card-text>
      <v-card-actions>
        <!-- Says why Continue is greyed out — the checkbox is easy to miss
             under the code. Gone once it is ticked. -->
        <div v-if="!acknowledged" class="text-caption text-medium-emphasis pl-2">
          Check the box above to continue.
        </div>
        <v-spacer />
        <v-btn
          variant="elevated"
          color="primary"
          :disabled="!acknowledged"
          @click="finish"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import fileDownload from 'js-file-download'
  import { copyText } from '@/services/ClipboardHelper'
  import { recoveryFileName, recoveryFileText, brandName } from '@/services/e2ee/nc1/RecoveryCodeFile'

  // Blocking require-acknowledge display of the NC1 recovery code, shown once
  // right after the signup bootstrap creates the identity. The code exists
  // only in this component's props — it is never persisted anywhere.
  export default {
    name: 'RecoveryCodeDialog',
    props: {
      modelValue: {
        type: Boolean,
        default: false
      },
      code: {
        type: String,
        default: ''
      },
      passkeyRegistered: {
        type: Boolean,
        default: false
      },
      // Opened from the Account page's "Get a backup code" (not the opt-in
      // fallback): the intro says so instead of "this device couldn't..."
      onRequest: {
        type: Boolean,
        default: false
      },
      // The account the code belongs to: written into the downloaded file and
      // its name so the unlock dialogs can tell whose file they were handed
      // (2026-10-10). Falls back to the signed-in account.
      email: {
        type: String,
        default: ''
      },
      // When the code was minted (ms since the epoch), from the mint site:
      // stamped into the file name and a `Created:` line so the newest of
      // several files is obvious (2026-10-10). Not "now" — Download can be
      // pressed any time this dialog is open. 0 = unknown = no stamp.
      mintedAt: {
        type: Number,
        default: 0
      }
    },
    emits: ['update:modelValue', 'acknowledged'],
    data() {
      return {
        acknowledged: false,
        copied: false,
        downloaded: false,
        emailHint: false
      }
    },
    computed: {
      accountEmail() {
        if(this.email) return String(this.email)
        const a = this.$store.state.account
        return (a && a.accountId) ? String(a.accountId) : ''
      },
      // The code mailed to the user's own address. mailto: can only prefill
      // To / Subject / Body — From is whatever account their mail program
      // sends as, which for nearly everyone is this same address.
      mailtoHref() {
        if(!this.accountEmail || !this.code) return null
        const subject = encodeURIComponent(`${brandName()} end-to-end encryption backup code`)
        const body = encodeURIComponent(this.recoveryText())
        // Keep '@' literal: every mail program accepts it, not all unescape %40.
        const to = encodeURIComponent(this.accountEmail).replace(/%40/g, '@')
        return `mailto:${to}?subject=${subject}&body=${body}`
      }
    },
    created() {
      this.emailWatch = null // non-reactive: listeners + timer of the last Email click
    },
    beforeUnmount() {
      this.clearEmailWatch()
    },
    methods: {
      async copy() {
        this.copied = await copyText(this.code)
        if(this.copied) {
          setTimeout(() => { this.copied = false }, 2000)
        }
      },
      // The file the unlock dialogs' "Load backup code file" reads. Name,
      // text and parse all live in RecoveryCodeFile.js; both name the account
      // so a file for someone else is caught on the way back in.
      recoveryText() {
        return recoveryFileText({ code: this.code, email: this.accountEmail, mintedAt: this.mintedAt })
      },
      download() {
        fileDownload(this.recoveryText(), recoveryFileName(this.accountEmail, { mintedAt: this.mintedAt }))
        this.downloaded = true
        // Saving the file IS the acknowledgement most people mean.
        this.acknowledged = true
      },
      // No API says whether a mailto: handler exists: a browser is equally
      // silent for "opened in the background" and "nothing registered", and
      // the Android app's WebView hands the link to the system chooser. So the
      // button shows everywhere, and what IS observable is watched instead:
      // a mail program, a Gmail tab or the OS "open with" chooser taking over
      // leaves this page hidden or its window unfocused. The link opens in a
      // new tab, which the browser usually closes again once the mail program
      // has it — so the page can be hidden and back within a moment of the
      // click whether or not anything took over, and the first blur / hidden
      // event proves nothing. The verdict is where things stand once the dust
      // has settled: still visible and focused = nothing opened, say so and
      // point at Copy / Download. The hint never hides the button — a mail
      // program opening behind the browser would look exactly like a miss.
      emailClicked() {
        this.emailHint = false
        this.clearEmailWatch()
        const onAway = () => this.clearEmailWatch() // the page itself is leaving
        const timer = setTimeout(() => {
          this.clearEmailWatch()
          this.emailHint = !document.hidden && document.hasFocus()
        }, 2500)
        window.addEventListener('pagehide', onAway)
        this.emailWatch = { onAway, timer }
      },
      clearEmailWatch() {
        if(!this.emailWatch) return
        const { onAway, timer } = this.emailWatch
        clearTimeout(timer)
        window.removeEventListener('pagehide', onAway)
        this.emailWatch = null
      },
      finish() {
        this.$emit('update:modelValue', false)
        this.$emit('acknowledged')
      }
    }
  }
</script>

<style scoped>
  .recovery-code {
    font-family: monospace;
    letter-spacing: 0.08em;
    word-break: break-all;
    user-select: all;
  }
</style>
